import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Home, 
    MessageCircle, 
    BookOpen, 
    PenTool, 
    Menu, 
    X,
    Sparkles,
    Globe,
    Languages
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export function Navbar() {
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showMultilingualModal, setShowMultilingualModal] = useState(false);
    const { language, toggleLanguage, t } = useLanguage();

    const navItems = [
        { path: '/', label: t('home'), icon: Home },
        { path: '/chat', label: t('conversations'), icon: MessageCircle },
        { path: '/exercises', label: t('exercises'), icon: BookOpen },
        { path: '/journal', label: t('journal'), icon: PenTool },
    ];

    return (
        <>
        <nav className="sticky top-0 z-50 bg-[#0A0F0D]/90 backdrop-blur-md border-b border-emerald-900/30">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="flex items-center gap-2 text-emerald-100"
                        data-testid="navbar-logo"
                    >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-heading text-xl font-semibold hidden sm:block text-emerald-100">
                            Iulian AI
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-2">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path || 
                                (item.path !== '/' && location.pathname.startsWith(item.path));
                            
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
                                        isActive 
                                            ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/30" 
                                            : "text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
                                    )}
                                >
                                    <Icon className="w-4 h-4" />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </div>

                    {/* Right side buttons */}
                    <div className="flex items-center gap-2">
                        {/* Multilingual Support Button */}
                        <button
                            onClick={() => setShowMultilingualModal(true)}
                            data-testid="multilingual-support-btn"
                            aria-label={t('multilingualSupport')}
                            className="flex items-center gap-1.5 px-3 py-2 bg-blue-900/50 text-blue-300 rounded-full text-sm font-medium hover:bg-blue-800/50 transition-all border border-blue-700/50"
                            title={t('multilingualSupport')}
                        >
                            <Languages className="w-4 h-4" aria-hidden="true" />
                            <span className="hidden sm:inline text-xs" aria-hidden="true">🌐</span>
                        </button>

                        {/* Language Toggle Button */}
                        <button
                            onClick={toggleLanguage}
                            data-testid="language-toggle"
                            aria-label={language === 'ro' ? 'Schimbă limba în Engleză' : 'Switch language to Romanian'}
                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-800/50 text-emerald-300 rounded-full text-sm font-medium hover:bg-emerald-700/50 transition-all border border-emerald-700/50"
                        >
                            <Globe className="w-4 h-4" aria-hidden="true" />
                            <span className="font-bold">{language.toUpperCase()}</span>
                        </button>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2 rounded-lg text-emerald-300 hover:bg-emerald-500/10"
                            data-testid="mobile-menu-toggle"
                            aria-label={mobileMenuOpen ? 'Închide meniul' : 'Deschide meniul'}
                            aria-expanded={mobileMenuOpen}
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden border-t border-emerald-900/30 bg-[#0A0F0D]"
                    >
                        <div className="px-4 py-4 space-y-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                                        className={cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all",
                                            isActive 
                                                ? "bg-emerald-600 text-white" 
                                                : "text-emerald-300 hover:bg-emerald-500/10"
                                        )}
                                    >
                                        <Icon className="w-5 h-5" />
                                        {item.label}
                                    </Link>
                                );
                            })}
                            
                            {/* Mobile Add Credits Button */}
                            <a
                                href="https://emergent.sh/settings/billing"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setMobileMenuOpen(false)}
                                data-testid="mobile-add-credits-btn"
                                className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium bg-emerald-600 text-white"
                            >
                                <CreditCard className="w-5 h-5" />
                                {t('addCredits')}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
        
        {/* Multilingual Support Modal - Outside nav for proper z-index */}
        <AnimatePresence>
            {showMultilingualModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={() => setShowMultilingualModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#0A0F0D] border-2 border-emerald-600/50 rounded-2xl p-6 max-w-md w-full shadow-2xl mx-4"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                                <Languages className="w-6 h-6 text-blue-400" />
                            </div>
                            <h3 className="font-heading text-lg text-emerald-50">
                                🌐 {language === 'ro' ? 'Suport Multilingv' : 'Multilingual Support'}
                            </h3>
                        </div>
                        <p className="text-emerald-200/80 mb-4 leading-relaxed text-sm">
                            {language === 'ro' 
                                ? 'Click dreapta oriunde pe pagină și selectează "Traduce în [Limba]" pentru a vizualiza în limba ta.'
                                : 'Right-click anywhere on the page and select "Translate to [Language]" to view in your native tongue.'}
                        </p>
                        <p className="text-emerald-200/80 mb-6 leading-relaxed text-sm">
                            {language === 'ro'
                                ? 'Pe mobil, apasă pe iconița (AA) sau (⋮) din meniul browser-ului.'
                                : 'On mobile, tap the (AA) or (⋮) icon in your browser menu.'}
                        </p>
                        <button
                            onClick={() => setShowMultilingualModal(false)}
                            data-testid="multilingual-ok-btn"
                            className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium transition-colors"
                        >
                            OK
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
        </>
    );
}

export default Navbar;
